/**
 * Where and how big in-page notifications are. Sizes are base (1x) px run through sc(), so they
 * grow with the same desktop UI scale as the rest of the interface (1x on touch devices);
 * nothing in the notification components carries its own px values.
 *
 * A mouse/trackpad screen is large and its top-right corner is busy (contacts, no-relief
 * buttons), so toasts drop in at the top centre there, where the eye already is. Touch devices
 * keep the top-right placement they always had.
 */
import { IS_FINE_POINTER, sc } from '@/utils/uiScale';

export type NotificationPlacement = 'top-center' | 'top-right';

export const NOTIFICATION_PLACEMENT: NotificationPlacement = IS_FINE_POINTER ? 'top-center' : 'top-right';

/** Base px (1x) — scaled by sc() below. */
const BASE = {
  top: 80,
  edge: 20,
  gap: 8,
  padY: 12,
  padX: 16,
  innerGap: 12,
  radius: 6,
  accent: 4,
  font: 14,
  icon: 18,
  close: 16,
  chip: 22,
  spinner: 16,
  minWidth: 300,
  maxWidth: 500,
};

export const NOTIFICATION_SIZE = {
  top: sc(BASE.top),
  edge: sc(BASE.edge),
  gap: sc(BASE.gap),
  padY: sc(BASE.padY),
  padX: sc(BASE.padX),
  innerGap: sc(BASE.innerGap),
  radius: sc(BASE.radius),
  accent: sc(BASE.accent),
  font: sc(BASE.font),
  icon: sc(BASE.icon),
  close: sc(BASE.close),
  chip: sc(BASE.chip),
  spinner: sc(BASE.spinner),
  minWidth: sc(BASE.minWidth),
  maxWidth: sc(BASE.maxWidth),
};

/** The fixed stack holding the toasts. */
export function notificationContainerStyle(placement: NotificationPlacement = NOTIFICATION_PLACEMENT) {
  const centered = placement === 'top-center';
  return {
    position: 'fixed' as const,
    top: NOTIFICATION_SIZE.top,
    // Centred: the stack spans the width and centres its toasts (a left:50% box would only be
    // given half the screen to wrap its text in).
    ...(centered ? { left: 0, right: 0 } : { right: NOTIFICATION_SIZE.edge }),
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: centered ? 'center' : 'flex-end',
    // The stack itself never swallows clicks meant for what is under it (the header, contacts).
    pointerEvents: 'none' as const,
  };
}

/** Entry animation: dropping in from above when centred, sliding in from the right otherwise. */
export const notificationAnimation = (placement: NotificationPlacement = NOTIFICATION_PLACEMENT) =>
  `${placement === 'top-center' ? 'notificationDrop' : 'slideIn'} 0.3s ease-out`;
